import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPendingClients, getTemplates, sendReviewRequests, sendSingleRequest } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Send, 
  Users, 
  Mail,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';

export default function ReviewRequests() {
  const { user } = useAuth();
  const [pendingClients, setPendingClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsData, templatesData] = await Promise.all([
        getPendingClients(),
        getTemplates()
      ]);
      setPendingClients(clientsData);
      setTemplates(templatesData);
      
      // Set default template
      const defaultTemplate = templatesData.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleAll = () => {
    if (selectedClients.length === pendingClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(pendingClients.map(c => c.id));
    }
  };

  const handleSendAll = async () => {
    if (!user?.google_review_link) {
      toast.error('Please configure your Google Review link in Settings first');
      return;
    }

    if (pendingClients.length === 0) {
      toast.error('No pending clients to send to');
      return;
    }

    setSending(true);
    try {
      const clientIds = selectedClients.length > 0 ? selectedClients : null;
      const result = await sendReviewRequests(clientIds, selectedTemplate || null);
      toast.success(result.message);
      setSelectedClients([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send review requests');
    } finally {
      setSending(false);
    }
  };

  const handleSendSingle = async (clientId) => {
    if (!user?.google_review_link) {
      toast.error('Please configure your Google Review link in Settings first');
      return;
    }

    try {
      await sendSingleRequest(clientId, selectedTemplate || null);
      toast.success('Review request sent!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-32 bg-muted rounded-xl" />
        <div className="h-96 bg-muted rounded-xl" />
      </div>
    );
  }

  const hasGoogleLink = !!user?.google_review_link;

  return (
    <div className="space-y-6" data-testid="review-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">Review Requests</h1>
          <p className="text-muted-foreground mt-1">
            Send review requests to your pending customers
          </p>
        </div>
      </div>

      {/* Warning if no Google Review link */}
      {!hasGoogleLink && (
        <Card className="border-amber-200 bg-amber-50 animate-fade-in" data-testid="google-link-warning">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Google Review link not configured</p>
                <p className="text-sm text-amber-700 mt-1">
                  Go to Settings to add your Google Review link before sending review requests.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send Controls */}
      <Card className="animate-fade-in" data-testid="send-controls">
        <CardContent className="py-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{pendingClients.length}</p>
                  <p className="text-sm text-muted-foreground">Pending requests</p>
                </div>
              </div>

              {selectedClients.length > 0 && (
                <Badge className="bg-primary text-primary-foreground">
                  {selectedClients.length} selected
                </Badge>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} {template.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handleSendAll}
                disabled={sending || !hasGoogleLink || pendingClients.length === 0}
                className="btn-animate"
                data-testid="send-all-btn"
              >
                {sending ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send {selectedClients.length > 0 ? `Selected (${selectedClients.length})` : 'All'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Clients List */}
      <Card className="animate-fade-in" data-testid="pending-clients">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Pending Customers</CardTitle>
            {pendingClients.length > 0 && (
              <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="select-all-btn">
                {selectedClients.length === pendingClients.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {pendingClients.length > 0 ? (
            <div className="divide-y divide-border">
              {pendingClients.map((client) => (
                <div 
                  key={client.id} 
                  className="flex items-center justify-between px-6 py-4 hover:bg-muted/50 transition-colors"
                  data-testid={`pending-client-${client.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={selectedClients.includes(client.id)}
                      onCheckedChange={() => toggleClient(client.id)}
                      data-testid={`checkbox-${client.id}`}
                    />
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      client.source === 'stripe' ? 'bg-violet-100' : 'bg-green-100'
                    }`}>
                      <span className={`text-sm font-semibold ${
                        client.source === 'stripe' ? 'text-violet-600' : 'text-green-600'
                      }`}>
                        {client.name?.charAt(0)?.toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{client.name}</p>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <Badge variant="outline" className={`${client.source === 'stripe' ? 'source-stripe' : 'source-shopify'} border`}>
                        {client.source}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(client.purchase_date)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendSingle(client.id)}
                      disabled={!hasGoogleLink}
                      className="btn-animate"
                      data-testid={`send-single-${client.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Send
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <CheckCircle2 className="empty-state-icon text-emerald-300" />
              <h3 className="empty-state-title">All caught up!</h3>
              <p className="empty-state-description">
                No pending review requests. New customers from Stripe and Shopify will appear here automatically.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
